import { Icon, Modal } from "@ttc3k/trekker";
import { Xmark } from "iconoir-react";
import { css } from "styled-system/css";
import { Box } from "styled-system/jsx";
import {
  PromotionDetails,
  type PromotionDetailsData,
} from "./PromotionDetails";

/** Solid dark 32px circle, 24px from the top-right corner (Promos > flyout close button). */
const closeTriggerClassName = css({
  bgColor: "ink.1000",
  borderRadius: "full",
  w: "32px",
  h: "32px",
  p: "0",
  top: "24px",
  right: "24px",
  left: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow:
    "0 4px 10px rgba(197, 189, 181, 0.3), 0 2.6px 6px rgba(197, 189, 181, 0.22), 0 2px 2.7px rgba(197, 189, 181, 0.14), 0 0.7px 1px rgba(197, 189, 181, 0.06)",
  _hover: { bgColor: "ink.900" },
  _focusVisible: { bgColor: "ink.900" },
});

export type PromotionSlideoutProps = {
  isOpen: boolean;
  onClose: () => void;
  promotion: PromotionDetailsData | null;
  /** "Learn more about <listing>" link; omitted when not provided. */
  onListingClick?: () => void;
  /** Render through a portal (off for the legacy, non-shadow-DOM widget build). */
  portal?: boolean;
  part?: string;
};

/**
 * Secondary slideout with a promotion's details, stacked on top of whatever
 * opened it: the admin promotion form (preview) or a widget's listing slideout.
 */
export const PromotionSlideout = ({
  isOpen,
  onClose,
  promotion,
  onListingClick,
  portal = true,
  part,
}: PromotionSlideoutProps) => (
  <Modal
    open={isOpen && !!promotion}
    onOpenChange={({ open }: { open: boolean }) => {
      if (!open) onClose();
    }}
    placement="rightLargePane"
    zIndex="max"
    closeTriggerVisual="subtle"
    closeTriggerPlacement="whereabouts"
    // Solid dark 32px circle over the hero (Promos > flyout close button)
    closeTriggerProps={{
      // Static css() so Panda emits these styles (prop objects aren't extracted)
      className: closeTriggerClassName,
      children: <Icon Element={Xmark} w="20px" h="20px" color="icon.inverse" />,
    }}
    noMaxH
    portal={portal}
  >
    <Box
      w={{ base: "100%", md: "520px" }}
      h="full"
      overflowY="auto"
      bg="ink.200"
      part={part}
    >
      {promotion && (
        <PromotionDetails
          promotion={promotion}
          onListingClick={onListingClick}
        />
      )}
    </Box>
  </Modal>
);
